import { tracked } from '@glimmer/tracking';

//////////////////////////////////////////////////////////////////////////////////////////////
// error-tracker - utility class that stores and manages the errors in the input components //
//////////////////////////////////////////////////////////////////////////////////////////////

export default class ErrorTracker {
  tabName = null;
  advisor = null;
  unparseableFields = [];
  unparseableValues = [];
  @tracked errorList = [];

  constructor(tabName, advisor) {
    this.tabName = tabName;
    this.advisor = advisor;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Utility Functions                                                          //
  ////////////////////////////////////////////////////////////////////////////////

  // Function that checks if the field has a parse error, value error or a cross-value error
  // Inputs: fieldName - the field to be checked in the errorList list
  // Returns: true if the fieldName is included in the list, otherwise false
  isInErrorList(fieldName) {
    return this.errorList.some((error) => {
      return error.fields.includes(fieldName);
    });
  }
  // Function that updates the unparseable list if the parameter status is valid or not
  // Inputs: param - field containing id, status and value
  updateUnparseableList(param) {
    if (param.status) {
      // Field good
      if (this.isUnparseable(param.fieldId)) {
        this.setUnparseable(param.fieldId, param.fieldValue, false);
      }
    } else {
      if (!this.isUnparseable(param.fieldId)) {
        this.setUnparseable(param.fieldId, param.fieldValue, true);
      } else {
        this.updateUnparsableValue(param.fieldId, param.fieldValue);
      }
    }
  }
  // Function that checks if the field has a parse error status
  // Inputs: fieldName - the field to be checked in the unparseableFields list
  // Returns: true if the fieldName is included in the list, otherwise false
  isUnparseable(fieldName) {
    return this.unparseableFields.includes(fieldName);
  }
  // Function that adds or removes a field from the unparseableFields list
  // Inputs: fieldName - the field to be checked if the parse status is valid
  //         fieldValue - the field corresponding to the fieldName
  //         failedParse - if true, the field is added to the unparseableFields list
  //                     - if false, the field is added to the unparseableFields list
  setUnparseable(fieldName, fieldValue, failedParse) {
    if (failedParse) {
      if (!this.unparseableFields.includes(fieldName)) {
        this.unparseableFields.push(fieldName);
        this.unparseableValues.push(fieldValue);
      }
    } else {
      if (this.unparseableFields.includes(fieldName)) {
        const index = this.unparseableFields.indexOf(fieldName);
        this.unparseableFields.splice(index, 1);
        this.unparseableValues.splice(index, 1);
      }
    }
  }
  // If the field is already in the list of unparseable fields, and the user has a different
  // error for that field, update the error in the unparseable values.
  updateUnparsableValue(fieldName, fieldValue) {
    const index = this.unparseableFields.indexOf(fieldName);
    this.unparseableValues.splice(index, 1, fieldValue);
  }
  // Returns true when the list of unparseable fields is not empty
  hasUnparsableFields() {
    return this.unparseableFields.length > 0;
  }
  // Function that gets the list of Field Parse Errors
  // Returns: fieldErrors - Array containing the list of errors that are sent to the controller
  getFieldErrors() {
    let fieldErrors = [];
    for (let i = 0; i < this.unparseableFields.length; i++) {
      let fieldId = this.unparseableFields[i];
      let fieldValue = this.unparseableValues[i];
      fieldErrors.push({
        tab: this.tabName,
        fields: [fieldId],
        id: fieldValue.length > 0 ? 'invalidField' : 'emptyField',
        parameters: { fieldId: fieldId, value: fieldValue },
      });
    }
    return fieldErrors;
  }
  // Function that does the following:
  // - Builds the List of all the field-parse errors for all fields in the component
  // - Calls the Value and Cross Value Validation Function for all Valid Fields
  // - Compiles all the list of errors for the component
  validate(workingModel) {
    // Include "FIELD" errors in the field error list and have them flag the field as unparseable
    // The errors can either be, Unparseable, or Empty
    let fieldErrors = this.getFieldErrors();

    // Call the Specifications Validation function to check parse-valid fields for value and cross value validation
    let valueErrors = this.advisor.validate(workingModel, this.unparseableFields);

    // Build the Error List
    let errors = fieldErrors.concat(valueErrors);
    this.errorList = errors;

    return errors;
  }
}
